import client from "./client";
import { AppThunk } from "../reducers";
import {
  fetchTorrentPending,
  fetchTorrentSuccess,
  fetchTorrentError,
  toggleAddingPending,
  toggleAddingDialog,
  addTorrentSuccess,
  addTorrentError,
  toggleDeletingPending,
  setToastMsg,
  toggleTorrentPending,
  setSettingPending,
  setSettings,
  setActiveTorrent,
  setSelectedTorrents,
  setActiveTorrentFiles,
  toggleArchivePending,
  setActiveTorrentTrackers,
  setArchiveQueue,
  syncDownloadStat,
  syncTorrentStats,
  setTorrentCount
} from "../actions";
import {
  AddingForm,
  Torrent,
  TorrentFile,
  isTorrentFolder,
  TorrentNode,
  TorrentFolder,
  Settings,
  QueueChangeData,
  DownloadStat,
  TorrentStatsDiff,
  Stat
} from "../types";

export const thunkFetchSettings = (): AppThunk => async dispatch => {
  dispatch(setSettingPending(true));
  try {
    const body = await client.get("/settings");
    dispatch(setSettings(body.data));
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  dispatch(setSettingPending(false));
};

export const thunkUpdateSettings = (
  settings: Settings
): AppThunk => async dispatch => {
  dispatch(setSettingPending(true));
  try {
    await client.put("/settings", { json: settings });
    dispatch(setSettings(settings));
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  dispatch(setSettingPending(false));
};

export const thunkFetchTorrents = (
  type: "reload" | "loadMore" | "loadAll" | "refresh" = "reload"
): AppThunk => async (dispatch, getState) => {
  const {
    search,
    torrent: { list, pending, size }
  } = getState();
  if (pending) {
    return;
  }
  dispatch(fetchTorrentPending());
  try {
    let limit, offset;
    if (type === 'reload') {
      limit = size;
    } else if (type === 'refresh') {
      limit = list.length || size;
    } else if (type === 'loadMore') {
      limit = size;
      offset = list.length;
    }
    const {
      data: { torrents, count }
    } = await client.get("/torrents", { qs: { ...search, offset, limit } });
    dispatch(fetchTorrentSuccess(torrents, type !== 'reload' && type !== 'refresh' && type !== 'loadAll'));
    // try to update active torrent as well
    if (type === 'refresh') {
      const active = getState().torrent.active;
      if (active && active.id) {
        let newActive = torrents.find((t: Torrent) => t.id === active.id);
        if (!newActive) {
          const body2 = await client.get("/torrents", {
            qs: { id: active.id }
          });
          newActive = body2.data[0];
        }
        if (newActive) {
          dispatch(setActiveTorrent(newActive));
        }
      }
    }
    dispatch(setTorrentCount(count));
    client.emit("reload");
  } catch (e: any) {
    dispatch(fetchTorrentError(e));
  }
};

export const thunkSubmitAdding = (
  form: AddingForm
): AppThunk => async dispatch => {
  dispatch(toggleAddingDialog(false));
  dispatch(toggleAddingPending(true));
  try {
    const body = await client.post("/torrents", { json: form });
    dispatch(addTorrentSuccess(body.data));
  } catch (e: any) {
    dispatch(addTorrentError(e));
  }
  dispatch(toggleAddingPending(false));
};

export const thunkDeleteTorrents = (
  torrentIds: number[],
  deleteFiles: boolean,
  reset = false
): AppThunk => async dispatch => {
  dispatch(toggleDeletingPending(true));
  try {
    await client.delete("/torrents", {
      json: { ids: torrentIds, deleteFiles }
    });
    if (reset) dispatch(setSelectedTorrents([]));
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  dispatch(toggleDeletingPending(false));
};

export const thunkPushDownload = (id: number): AppThunk => async dispatch => {
  dispatch(toggleTorrentPending(id, true));
  try {
    await client.put(`/torrents/${id}/push`);
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  dispatch(toggleTorrentPending(id, false));
};

export const thunkAddSubtitle = (
  id: number,
  filePath: string,
  dataB64: string
): AppThunk => async dispatch => {
  const tmp = filePath.split(/[\\/]/);
  dispatch(toggleTorrentPending(id, true));
  try {
    await client.post(`/torrents/${id}/subtitles`, {
      json: {
        filename: tmp[tmp.length - 1],
        dataB64
      }
    });
    dispatch(thunkFetchTorrents('refresh'))
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  dispatch(toggleTorrentPending(id, false));
};

export const thunkDelSubtitle = (
  id: number,
  subtitle: string
): AppThunk => async dispatch => {
  dispatch(toggleTorrentPending(id, true));
  try {
    await client.delete(`/torrents/${id}/subtitles`, {
      json: {
        subtitles: [subtitle]
      }
    });
    dispatch(thunkFetchTorrents('refresh'))
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  dispatch(toggleTorrentPending(id, false));
};

export const thunkRenameSubtitle = (
  id: number,
  oldName: string,
  newName: string
): AppThunk => async dispatch => {
  dispatch(toggleTorrentPending(id, true));
  try {
    await client.post(`/torrents/${id}/subtitles/rename`, {
      json: {
        oldName,
        newName
      }
    });
    dispatch(thunkFetchTorrents('refresh'))
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  dispatch(toggleTorrentPending(id, false));
};

export const thunkUpdateTorrent = (
  id: number,
  data: {
    title?: string;
    season?: number | null;
    episode?: number | null;
  }
): AppThunk => async dispatch => {
  dispatch(toggleTorrentPending(id, true));
  try {
    await client.put(`/torrents/${id}`, {
      json: data
    });
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  dispatch(toggleTorrentPending(id, false));
};

// update folder wanted status
function syncFolderWanted(folder: TorrentFolder): "all" | "partial" | "none" {
  const l = folder.children.filter(node =>
    isTorrentFolder(node) ? syncFolderWanted(node) : node.wanted
  ).length;
  folder.wanted =
    l === 0 ? "none" : l === folder.children.length ? "all" : "partial";
  return folder.wanted;
}

export const thunkFetchTorrentFiles = (
  id: number,
  hash: string
): AppThunk => async dispatch => {
  dispatch(toggleTorrentPending(id, true));
  try {
    const body = await client.get(`/files/${hash}`);
    const files = body.data as TorrentFile[];
    // convert to tree structure
    const root: TorrentNode[] = [];
    let i = 0;
    for (const file of files) {
      file.indice = i++;
      const parts = file.name.split("/");
      let children = root;
      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        // create parent ancest folders
        let folder = children
          .filter(isTorrentFolder)
          .find(f => f.name === part);
        if (!folder) {
          folder = { name: part, children: [], wanted: "all" };
          children.push(folder);
        }
        children = folder.children;
      }
      file.filename = parts[parts.length - 1];
      children.push(file);
    }

    root.filter(isTorrentFolder).forEach(syncFolderWanted);

    dispatch(setActiveTorrentFiles(id, files, root));
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  dispatch(toggleTorrentPending(id, false));
};

export const thunkFetchTorrentTrackers = (
  id: number,
  hash: string
): AppThunk => async dispatch => {
  dispatch(toggleTorrentPending(id, true));
  try {
    const body = await client.get(`/trackers/${hash}`);
    dispatch(setActiveTorrentTrackers(id, body.data));
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  dispatch(toggleTorrentPending(id, false));
};

export const thunkToggleTorrentFilesWanted = (
  id: number,
  hash: string,
  indices: number[],
  wanted: boolean
): AppThunk => async dispatch => {
  dispatch(toggleTorrentPending(id, true));
  try {
    await client.put(`/files/${hash}/wanted`, {
      json: {
        indices,
        wanted
      }
    });
    dispatch(thunkFetchTorrentFiles(id, hash));
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  dispatch(toggleTorrentPending(id, false));
};

export const thunkRenameTorrentFile = (
  id: number,
  hash: string,
  path: string,
  name: string
): AppThunk => async dispatch => {
  dispatch(toggleTorrentPending(id, true));
  try {
    await client.post(`/files/${hash}/rename`, {
      json: {
        path,
        name
      }
    });
    dispatch(thunkFetchTorrentFiles(id, hash));
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  dispatch(toggleTorrentPending(id, false));
};

export const thunkPauseTorrents = (ids: number[], hashes: string[]): AppThunk => async dispatch => {
  ids.forEach(id => dispatch(toggleTorrentPending(id, true)));
  try {
    await client.put(`/pause`, {
      json: {
        hashes
      }
    });
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  ids.forEach(id => dispatch(toggleTorrentPending(id, false)));
};

export const thunkResumeTorrents = (ids: number[], hashes: string[]): AppThunk => async dispatch => {
  ids.forEach(id => dispatch(toggleTorrentPending(id, true)));
  try {
    await client.put(`/resume`, {
      json: {
        hashes
      }
    });
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  ids.forEach(id => dispatch(toggleTorrentPending(id, false)));
};

export const thunkArchiveTorrents = (
  ids: number[],
  dest: string
): AppThunk => async dispatch => {
  dispatch(toggleArchivePending(true));
  try {
    await client.post("/archive", {
      json: {
        torrentIds: ids,
        archiveDest: dest
      }
    });
  } catch (e: any) {
    dispatch(setToastMsg(e.message, "error"));
  }
  dispatch(toggleArchivePending(false));
};

export const thunkConnectSocket = (): AppThunk => async dispatch => {
  client.connectSocket({
    queueChanged({ current, queue }: QueueChangeData) {
      if (current) {
        current.isCurrent = true;
        queue.unshift(current);
      }
      dispatch(setArchiveQueue(queue));
      dispatch(thunkFetchTorrents('refresh'));
    },
    queue({ current, queue }: QueueChangeData) {
      if (current) {
        current.isCurrent = true;
        queue.unshift(current);
      }
      dispatch(setArchiveQueue(queue));
    },
    stat({ dlStat, trStats }: Stat) {
      dispatch(syncDownloadStat(dlStat));
      dispatch(syncTorrentStats({ updated: trStats, removed: [] }));
    },
    dlStat(data: DownloadStat) {
      dispatch(syncDownloadStat(data));
    },
    trStats(data: TorrentStatsDiff) {
      dispatch(syncTorrentStats(data));
    },
    torrentsChanged() {
      dispatch(thunkFetchTorrents("refresh"))
    }
  });
};

export const thunkAuthSocket = (): AppThunk => async dispatch => {
  try {
    await client.authSocket();
  } catch (e: any) {
    console.error(e.stack);
    dispatch(setToastMsg(e.message));
  }
}